/**
 * @fileoverview This file contains the useS3Bucket hook for interacting with an AWS S3 bucket.
 * It provides functions for uploading, downloading, listing, and deleting files and folders.
 */

import { useAppSelector } from '@store/index';
import useToast from './useToast';
import {
  useDeleteFilesMutation,
  useEncryptFileMutation,
  useLazyGetDownloadFileQuery,
  useLazyGetDownloadMultipleFileQuery,
  usePostUploadFilesMutation,
} from '@api/files.api.rtk';
import { SHA512 } from 'crypto-js';
import JSZip from 'jszip';
import {
  DownloadFileParams,
  IFileDownloadMultiplePayload,
  UploadFileParams,
} from '@interfaces/files.interface';
import { iS3Module } from '@interfaces/module.interface';

/**
 * Type definition for the useS3Bucket hook.
 */
export type UseS3BucketType = {
  /**
   * Encrypts a file and sets the encrypted base64 string.
   * @param file - The file to be encrypted.
   * @param setEncryptedBase64 - Callback to set the encrypted base64 string.
   * @param setStatus - Optional callback to set the status.
   * @param disableToast - Optional flag to disable toast notifications.
   * @returns A promise that resolves to a boolean indicating success or undefined.
   */
  encryptFile: (
    file: File,
    setEncryptedBase64: (encryptedBase64: string) => void,
    setStatus?: (status: number) => void,
    disableToast?: boolean,
  ) => Promise<boolean | undefined>;

  /**
   * Uploads a file to the S3 bucket.
   * @param selectedData - The selected data as a string.
   * @param files - Array of files to be uploaded.
   * @param description - Description of the upload.
   * @param module - The module to which the file belongs.
   * @param disableToast - Flag to disable toast notifications.
   * @returns A promise that resolves when the upload is complete.
   */
  uploadFile: (
    selectedData: string,
    files: UploadFileParams[],
    description: string,
    module: iS3Module,
    disableToast?: boolean,
    isSingleRelation?: boolean,
  ) => Promise<void>;

  /**
   * Retrieves a file from the S3 bucket.
   * @param fileID - The file ID to retrieve.
   * @returns A promise that resolves to the file content as a string or null if not found.
   */
  getFile: (fileID: string, validity?: number) => Promise<string | null>;
  getFileBlob: (fileID: string, validity?: number) => Promise<Blob | null>;

  /**
   * Retrieves a file from the S3 bucket.
   * @param fileID - The file ID to retrieve.
   * @returns A promise that resolves to the file content as a string or null if not found.
   */
  getMultipleFiles: (fileIDs: string[]) => Promise<
    | {
        file: File;
        fileName: string;
        url: string;
        fileID: string;
        blobFile: Blob;
      }[]
    | null
  >; // Updated type
  /**
   * Deletes a file from the S3 bucket.
   * @param fileID - The ID of the file to delete.
   * @param disableToast - Optional flag to disable toast notifications.
   * @returns A promise that resolves when the file is deleted.
   */
  deleteFile: (fileID: string, disableToast?: boolean) => Promise<void>;
  /**
   * Deletes multiple files from the S3 bucket.
   * @param fileIDs - Array of file IDs to delete.
   * @param disableToast - Optional flag to disable toast notifications.
   * @returns A promise that resolves when the files are deleted.
   */
  deleteMultipleFile: (
    fileIDs: string[],
    module?: iS3Module,
    disableToast?: boolean,
  ) => Promise<void>;
  /**
   * Downloads multiple files from the S3 bucket.
   * @param files - Array of files to be downloaded.
   * @param setStatus - Optional callback to set the status.
   * @param disableToast - Optional flag to disable toast notifications.
   * @returns A promise that resolves when the download is complete.
   */
  downloadFiles: (
    files: DownloadFileParams[],
    setStatus?: (status: number) => void,
    disableToast?: boolean,
  ) => Promise<void>;
};

/** Maximum file size allowed for upload (25MB) */
const MAX_FILE_SIZE = 25 * 1024 * 1024;

/**
 * Custom hook for interacting with an AWS S3 bucket
 * @returns {UseS3BucketType} Object containing functions for S3 operations
 */
const useS3Bucket = (): UseS3BucketType => {
  const selectedPropertyID = useAppSelector(
    (state) => state.properties.selectedProperty.value,
  );
  const { toast } = useToast();
  const [encryptFiles] = useEncryptFileMutation();
  const [getDownloadFile] = useLazyGetDownloadFileQuery();
  const [deleteFiles] = useDeleteFilesMutation();
  const [sendFileToS3] = usePostUploadFilesMutation();
  const [getDownloadMultipleFile] = useLazyGetDownloadMultipleFileQuery();

  /**
   * Converts a given file to a Base64 encoded string.
   *
   * @param {File} file - The file to be converted.
   * @returns {Promise<string>} A promise that resolves to the Base64 encoded string of the file's contents.
   *
   * @example
   * ```typescript
   * const file = new File(["content"], "example.txt");
   * convertFileToBase64(file).then(base64String => {
   *   console.log(base64String);
   * });
   * ```
   *
   * @throws Will reject the promise with an empty string if there is an error during the file reading process.
   */
  const convertFileToBase64 = (file: File) => {
    const reader = new FileReader();
    return new Promise<string>((resolve, reject) => {
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = (error) => {
        console.error('Error converting file to base64:', error);
        reject('');
      };
    });
  };

  /**
   * Encrypts a file and updates the encrypted base64 string.
   *
   * @param {File} file - The file to be encrypted.
   * @param {(encryptedBase64: string) => void} setEncryptedBase64 - Callback to set the encrypted base64 string.
   * @param {(status: number) => void} [setStatus] - Optional callback to set the upload status as a percentage.
   * @param {boolean} [disableToast=true] - Flag to disable toast notifications.
   * @returns {Promise<boolean>} - Returns a promise that resolves to true if the file was successfully encrypted, otherwise false.
   *
   * @throws Will throw an error if the file size exceeds the maximum allowed size.
   */
  const encryptFile = async (
    file: File,
    setEncryptedBase64: (encryptedBase64: string) => void,
    setStatus?: (status: number) => void,
    disableToast: boolean = true,
  ) => {
    if (file.size > MAX_FILE_SIZE) {
      await toast({
        message: 'File should be less than 25MB',
        severity: 'error',
      });
      return false;
    }

    try {
      const base64Data = await convertFileToBase64(file); // Convert file to base64
      // Prepare payload
      const payload = {
        properties: [
          {
            property: {
              files: [
                {
                  file: {
                    base64Data,
                  },
                },
              ],
            },
          },
        ],
      };
      // Encrypt file
      await encryptFiles(payload)
        .unwrap()
        .then((result) => {
          if (setStatus) {
            let uploaded = 0;
            const total = file.size;
            const interval = setInterval(() => {
              uploaded += total * 0.1; // Simulate 10% progress increments
              setStatus(Math.min(100, Math.floor((uploaded / total) * 100)));
              if (uploaded >= total) {
                clearInterval(interval);
              }
            }, 100);
          }

          setEncryptedBase64(result); // Set encrypted base64 string
        })
        .catch((error) => {
          console.error(error);
        });

      !disableToast &&
        (await toast({
          message: 'Successfully uploaded file.',
          severity: 'success',
        }));
      return true;
    } catch (error) {
      console.error(error);
      await toast({ message: 'Failed uploading of file', severity: 'error' });
      return false;
    }
  };

  /**
   * Uploads files to an S3 bucket with the provided parameters.
   *
   * @param selectedData - The ID of the selected data to which the files are related.
   * @param files - An array of file parameters to be uploaded.
   * @param description - A description of the files being uploaded.
   * @param module - The module to which the files belong. Can be one of 'Rooms', 'Business Block', 'Profiles', 'Products', 'Projects', or 'Others'.
   * @param disableToast - A boolean flag to disable toast notifications. Defaults to true.
   * @param isSingleRelation
   * @returns A promise that resolves when the files are successfully uploaded.
   *
   * @throws Will throw an error if the upload fails.
   */
  const uploadFile = async (
    selectedData: string,
    files: UploadFileParams[],
    description: string,
    module: iS3Module,
    disableToast: boolean = true,
    isSingleRelation: boolean = false,
  ) => {
    // add option to delete file if single relation only
    try {
      // Check if there are files to upload
      if (files.length === 0) {
        await toast({ message: 'No files to upload', severity: 'warning' });
        return;
      }

      isSingleRelation && (await deleteFile(selectedData, true));

      // Prepare payload
      const payload = {
        properties: [
          {
            property: {
              propertyID: selectedPropertyID,
              files: files?.map((file) => {
                const { fileData, fileEncryptedData } = file;
                return {
                  file: {
                    module: module,
                    referenceID: selectedData!,
                    description: description,
                    fileName: fileData.name,
                    fileExt: fileData.name.split('.')?.pop() || 'no extension',
                    fileSize: fileData.size.toString(),
                    fileMimeType: fileData.type || 'application/octet-stream',
                    base64Data: fileEncryptedData,
                    signature: SHA512(
                      `${selectedPropertyID}${module}${selectedData}${description}${fileData.name}${
                        fileData.name.split('.')?.pop()?.toLocaleLowerCase() ||
                        'no extension'
                      }${fileData.type?.toLocaleLowerCase() || 'application/octet-stream'}${fileData.size}`,
                    ).toString(),
                  },
                };
              }),
            },
          },
        ],
      };
      // Upload files
      await sendFileToS3(payload)
        .unwrap()
        .then(() => {
          !disableToast &&
            toast({
              severity: 'success',
              message: 'Successfully attached file(s).',
            });
        })
        .catch((err) => toast({ severity: 'error', message: err }));
    } catch (error) {
      console.error(error);
      await toast({ message: 'Failed upload a file', severity: 'error' });
    }
  };

  /**
   * Retrieves a file from the S3 bucket
   * @param {string} fileID - Name of the file to retrieve
   * @returns {Promise<Object|undefined>} File details if successful, undefined otherwise
   */
  const getFile = async (
    fileID: string,
    validity?: number,
  ): Promise<string | null> => {
    try {
      // Get file from S3
      const response = await getDownloadFile({ id: fileID, validity });

      // Check if file was retrieved
      if (response.data) {
        const fileBlob = response.data;
        return URL.createObjectURL(fileBlob);
      } else {
        return null;
      }
    } catch (error) {
      console.error(error);
      await toast({
        message: `Error downloading ${fileID}`,
        severity: 'error',
      });
      return null;
    }
  };

  const getFileBlob = async (
    fileID: string,
    validity?: number,
  ): Promise<Blob | null> => {
    try {
      // Get file from S3
      const response = await getDownloadFile({ id: fileID, validity });

      // Check if file was retrieved
      if (response.data) {
        return response.data;
      } else {
        return null;
      }
    } catch (error) {
      console.error(error);
      await toast({
        message: `Error downloading ${fileID}`,
        severity: 'error',
      });
      return null;
    }
  };

  /**
   * Retrieves multiple files from the S3 bucket and extracts them from a ZIP file.
   * @param {string[]} fileIDs - Array of file IDs to retrieve.
   * @returns {Promise<{ fileName: string; url: string; fileID: string }[] | null>} A promise that resolves to an array of file details (name and URL) if successful, or `null` if not found.
   */
  const getMultipleFiles = async (
    fileIDs: string[],
  ): Promise<
    | {
        file: File;
        fileName: string;
        url: string;
        fileID: string;
        blobFile: Blob;
      }[]
    | null
  > => {
    try {
      // Return null if no fileIDs are given
      if (!!!fileIDs.length) {
        return null;
      }

      // Prepare the payload for downloading multiple files
      // Prepare the payload for downloading multiple files
      const payload: IFileDownloadMultiplePayload = {
        fileIDs: fileIDs.map((id) => ({ fileID: id })), // Map strings to objects with fileID property
      };

      // Get files from S3
      const response = await getDownloadMultipleFile(payload);

      // Check if the response contains a ZIP file
      if (
        response.data &&
        response.data instanceof Blob &&
        response.data.type === 'application/zip'
      ) {
        const zip = new JSZip();
        const zipContent = await zip.loadAsync(response.data); // Load the ZIP file
        const files: {
          file: File;
          fileName: string;
          url: string;
          fileID: string;
          blobFile: Blob;
        }[] = [];
        // Iterate over each file in the ZIP
        for (const fileName of Object.keys(zipContent.files)) {
          const fileData = await zipContent.files[fileName].async('blob'); // Extract file as Blob

          // Extract fileID and fileName from the ZIP file name
          const [fileID, ...fileNameParts] = fileName.split('_'); // Split by `_` to separate fileID and fileName
          const actualFileName = fileNameParts.join('_'); // Rejoin the remaining parts as the actual file name
          const file = new File([fileData], actualFileName, {
            type: fileData.type,
          }); // Convert Blob to File
          const fileUrl = URL.createObjectURL(fileData); // Create a Blob URL
          files.push({
            file: file,
            fileName: actualFileName,
            url: fileUrl,
            fileID: fileID,
            blobFile: fileData,
          }); // Add file details to the array
        }

        return files; // Return the array of file details
      } else {
        return null;
      }
    } catch (error) {
      console.error(error);
      await toast({
        message: `Error extracting files from ZIP.`,
        severity: 'error',
      });
      return null;
    }
  };

  /**
   * Deletes a file from the S3 bucket
   * @param {string} fileID - File Reference ID to delete
   * @param {boolean} [disableToast=false] - Whether to disable toast notifications
   * @returns toast notification
   */
  const deleteFile = async (fileID: string, disableToast: boolean = false) => {
    try {
      // Prepare payload
      const payload = {
        properties: [
          {
            property: {
              propertyID: selectedPropertyID,
              files: [
                {
                  file: {
                    fileReferenceID: fileID,
                    signature: SHA512(
                      `${selectedPropertyID}${fileID}`,
                    ).toString(),
                  },
                },
              ],
            },
          },
        ],
      };
      // Delete file
      await deleteFiles(payload)
        .unwrap()
        .then(() => {
          !disableToast &&
            toast({
              message: 'Successfully deleted file.',
              severity: 'success',
            });
        })
        .catch((_err) =>
          toast({ message: 'Error deleting file: ' + _err, severity: 'error' }),
        );
    } catch (error) {
      await toast({
        message: 'Error deleting file: ' + error,
        severity: 'error',
      });
    }
  };

  /**
   * Deletes a file from the S3 bucket
   * @param {string[]} fileIDs - Array of file IDs to retrieve.
   * @param {boolean} [disableToast=false] - Whether to disable toast notifications
   * @returns toast notification
   */
  const deleteMultipleFile = async (
    fileIDs: string[],
    module?: iS3Module,
    disableToast: boolean = false,
  ) => {
    try {
      // Prepare payload
      const payload = {
        properties: [
          {
            property: {
              propertyID: selectedPropertyID,
              files: fileIDs.map((fileID) => ({
                file: {
                  fileReferenceID: fileID,
                  module,
                  signature: SHA512(
                    `${selectedPropertyID}${fileID}`,
                  ).toString(),
                },
              })),
            },
          },
        ],
      };

      // Delete file
      await deleteFiles(payload)
        .unwrap()
        .then(() => {
          !disableToast &&
            toast({
              message: 'Successfully deleted files.',
              severity: 'success',
            });
        })
        .catch((_err) =>
          toast({
            message: 'Error deleting files: ' + _err,
            severity: 'error',
          }),
        );
    } catch (error) {
      await toast({
        message: 'Error deleting files: ' + error,
        severity: 'error',
      });
    }
  };

  /**
   * Downloads multiple files from the S3 bucket
   * @param {DownloadFileParams[]} files - Array of file information to download
   * @param {function} [setStatus] - Optional function to update download status
   * @param {boolean} [disableToast=true] - Whether to disable toast notifications
   * @returns {Promise<void>}
   */
  const downloadFiles = async (
    files: DownloadFileParams[],
    setStatus?: (status: number) => void,
    disableToast: boolean = true,
  ) => {
    // Stop function if no files are attached
    if (!!!files.length) {
      return;
    }

    const downloadSingleFile = async (file: DownloadFileParams) => {
      const { fileName, fileID } = file; // Extract file information

      try {
        // Get file from S3
        const response = await getDownloadFile({ id: fileID });
        if (response.data) {
          const fileBlob = response.data;
          // Create a URL for the Blob
          const blobUrl = window.URL.createObjectURL(fileBlob);
          // Create a link element
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName; // Specify the file name

          // Append link to the body, trigger click, and remove the link
          document.body.appendChild(link);
          link.click();
          link.remove();

          // Revoke the object URL
          window.URL.revokeObjectURL(blobUrl);

          // Set progress to 100%
          if (setStatus) {
            setStatus(100);
          }

          if (!disableToast) {
            toast({
              message: `Successfully downloaded file ${fileName}.`,
              severity: 'success',
            });
          }
        } else {
          toast({
            message: `Error downloading ${fileName}`,
            severity: 'error',
          });
        }
      } catch (error) {
        console.error(error);
        toast({
          message: `Error downloading ${fileName}`,
          severity: 'error',
        });
      }
    };

    // Download files sequentially
    for (const file of files) {
      await downloadSingleFile(file);
    }
  };

  return {
    encryptFile,
    uploadFile,
    getFile,
    deleteFile,
    deleteMultipleFile,
    downloadFiles,
    getMultipleFiles,
    getFileBlob,
  };
};

export default useS3Bucket;
